import React, { useState, useEffect } from 'react';
import { Modal, Input, TreeSelect, Button, Spin, Form, notification } from 'antd';
import { createState } from '../services/adminApi';
import { updateState } from '../services/statesApi';
import { State } from '../@types/state';

interface TreeNode {
    label: string;
    value: number;
    disabled?: boolean;
    children?: TreeNode[];
}

interface StateModalProps {
    visible: boolean;
    onClose: () => void;
    states: TreeNode[];
    editingState?: State | null;
    onSuccess: (parentId: number, newState: State) => void;
}

const StateModal: React.FC<StateModalProps> = ({ 
    visible, 
    onClose, 
    states, 
    editingState, 
    onSuccess 
}) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const isEditing = !!editingState;

    useEffect(() => {
        if (visible) {
            if (isEditing && editingState) {
                form.setFieldsValue({
                    name: editingState.name,
                    parentId: editingState.parentId
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    name: '',
                    parentId: undefined
                });
            }
        }
    }, [visible, editingState, isEditing, form]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setIsLoading(true);

            if (isEditing && editingState) {
                const updatedState = await updateState(editingState.id, values.name, values.parentId);
                onSuccess(values.parentId, updatedState);
                notification.success({ message: 'Филиал успешно обновлен' });
            } else {
                const newState = await createState(values.parentId, values.name, 'F', false);
                onSuccess(values.parentId, newState);
                notification.success({ message: 'Филиал успешно создан' });
            }
            
            handleClose();
        } catch (error) {
            notification.error({ 
                message: isEditing ? 'Ошибка при обновлении филиала' : 'Ошибка при создании филиала' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            title={isEditing ? "Редактирование филиала" : "Создание нового филиала"}
            open={visible}
            onCancel={handleClose}
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={isLoading}>
                    Закрыть
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={isLoading} 
                    onClick={handleSave}
                >
                    Сохранить
                </Button>
            ]}
        >
            <Spin spinning={isLoading}>
            <Form form={form} layout="horizontal" style={{ marginTop: 20 }}>
                    <Form.Item
                        name="name"
                        label="Название филиала"
                        rules={[{ required: true, message: 'Введите название филиала' }]}
                    >
                        <Input 
                            placeholder="Введите название филиала" 
                            disabled={isLoading}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        name="parentId"
                        label="Филиальная сеть"
                        rules={[{ required: true, message: 'Выберите филиальную сеть' }]}
                    >
                        <TreeSelect
                            style={{ width: '100%' }}
                            treeData={states}
                            placeholder="Выберите филиальную сеть"
                            treeDefaultExpandAll={true}
                            disabled={isLoading}
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default StateModal; 